import * as React from "react";
import { LucideIcon, icons } from "lucide-react";

export interface TileOption {
  value: string;
  label: string;
  icon?: keyof typeof icons;
  indicator?: boolean;
}

interface TileSelectorProps {
  options: TileOption[];
  selectedValues: string[];
  onChange: (newValues: string[]) => void;
  className?: string;
  name?: string;
  multiSelect?: boolean;
}

const TileSelector: React.FC<TileSelectorProps> = ({
  options,
  selectedValues,
  onChange,
  className = "",
  name = "tile-selector",
  multiSelect = true,
}) => {
  const scrollContainerRef = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    const hasSeenHint = localStorage.getItem("has-seen-scroll-hint");
    const container = scrollContainerRef.current;

    if (
      !hasSeenHint &&
      container &&
      container.scrollWidth > container.clientWidth
    ) {
      // Custom smooth scroll function for precise control
      const animateScroll = (target: number, duration: number) => {
        const start = container.scrollLeft;
        const change = target - start;
        const startTime = performance.now();

        const animate = (currentTime: number) => {
          const elapsed = currentTime - startTime;
          const progress = Math.min(elapsed / duration, 1);

          // Easing function: easeInOutQuad
          const ease =
            progress < 0.5
              ? 2 * progress * progress
              : 1 - Math.pow(-2 * progress + 2, 2) / 2;

          container.scrollLeft = start + change * ease;

          if (progress < 1) {
            requestAnimationFrame(animate);
          }
        };
        requestAnimationFrame(animate);
      };

      const timer = setTimeout(() => {
        // 1. Slowly scroll right (800ms)
        animateScroll(60, 800);

        // 2. Wait, then slowly scroll back (800ms)
        setTimeout(() => {
          animateScroll(0, 800);
          localStorage.setItem("has-seen-scroll-hint", "true");
        }, 1200);
      }, 300);

      return () => clearTimeout(timer);
    }
  }, []);

  const handleSelect = (value: string) => {
    const isSelected = selectedValues.includes(value);
    const newSelectedValues = isSelected
      ? selectedValues.filter((v) => v !== value)
      : multiSelect
        ? [...selectedValues, value]
        : [value];
    onChange(newSelectedValues);
  };

  return (
    <div
      ref={scrollContainerRef}
      className={`my-4 flex gap-4 overflow-x-auto px-1 pt-1 pb-2 ${className}`}
      role="group"
      aria-labelledby={name ? `${name}-label` : undefined}
    >
      {options.map((option) => {
        const isSelected = selectedValues.includes(option.value);
        const IconComponent: LucideIcon | undefined = option.icon
          ? icons[option.icon]
          : undefined;

        return (
          <div
            key={option.value}
            data-option-value={option.value}
            className={`focus-visible:ring-soluud-primary relative flex h-20 w-20 flex-shrink-0 cursor-pointer flex-col items-center justify-center rounded-lg border p-2 text-center transition-all duration-200 focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-none ${
              isSelected
                ? "border-soluud-primary bg-soluud-primary-light text-soluud-primary"
                : "hover:border-soluud-primary focus:border-soluud-primary border-gray-300 bg-white text-gray-800"
            }`}
            onClick={() => handleSelect(option.value)}
            tabIndex={0}
            role="checkbox"
            aria-checked={isSelected}
            aria-labelledby={`${name}-${option.value}-label`}
          >
            {/* Indicator Dot */}
            {option.indicator && (
              <div className="bg-soluud-primary absolute top-1 right-1 h-2 w-2 rounded-full shadow-sm" />
            )}

            {IconComponent && (
              <IconComponent size={24} className="mb-1 flex-shrink-0" />
            )}
            <span
              id={`${name}-${option.value}-label`}
              className="text-xs leading-tight font-medium break-words"
            >
              {option.label}
            </span>
          </div>
        );
      })}
    </div>
  );
};

export default TileSelector;
